import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FirmwareVersion, Gateway } from '@app/api/building/gateway/Gateway';
import { GatewayHealth, GatewayInstalledSnap, Gateways, GatewayService } from '@app/shared/services/gateway.service';
import { NavigationSection } from '@app/shared/services/navigation/navigation-section';
import { UserService } from '@app/shared/services/user/user.service';
import { concatMap, from, Observable, of, Subject } from 'rxjs';
import { finalize, map, switchMap, toArray } from 'rxjs/operators';
import {
  CompareConfigDialogComponent,
  DialogData
} from '@app/administration/gateway/compare-config-dialog/compare-config-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { NavigationService } from '@app/shared/services/navigation/navigation.service';
import { MatTableDataSource } from '@angular/material/table';
import { ToastService } from '@services/toast/toast.service';
import { ConfirmationDialogService } from '@services/confirmation-dialog/confirmation-dialog.service';
import { ConfirmDialogData } from '@components/dialogs/confirm/confirm.component';
import { AsyncPipe } from '@angular/common';
import { FormGatewayDetailsComponent } from './form-gateway-details/form-gateway-details.component';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-gateway',
  templateUrl: './gateway.component.html',
  styleUrls: ['./gateway.component.scss'],
  imports: [FormGatewayDetailsComponent, MatButton, AsyncPipe, MatProgressSpinner]
})
export class AdministrationGatewaysComponent implements OnInit {
  public gateways$: Observable<GatewayWithHealth[]>;
  public newGatewayDetails: Gateway;
  public isBusy = true;
  public buildingId: number;
  public disableFloorsMenu = true;
  public tabs: NavigationSection[];
  public afterUpdate$: Subject<Gateway> = new Subject();
  public onError$: Subject<Gateway> = new Subject();
  public onGatewayReset$: Subject<number> = new Subject();
  private errorMessage =
    'Please ensure the gateway address is entered correctly and all required fields are filled in.';
  dataSource = new MatTableDataSource<GatewayWithHealth>();

  isGatewayListBusy = signal(false);
  isLoading = signal(false);

  constructor(
    private route: ActivatedRoute,
    private gatewayService: GatewayService,
    private buildingService: UserService,
    private dialog: MatDialog,
    private navigationService: NavigationService,
    private toastService: ToastService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    this.route.params
      .pipe(
        concatMap((params) => {
          const { buildingId } = params;
          return this.buildingService.getBuilding(buildingId);
        })
      )
      .subscribe((building) => {
        this.buildingId = building.id;
        this.resetNewGatewayForm();
        this.init();
        this.navigationService.initNavigation(window.location.href, building);
      });
  }

  private init(): void {
    this.isBusy = false;
    this.loadGateways();
    this.updateGatewayFirmwareVersions();
  }

  private loadGateways(): void {
    this.startBusy();
    this.gateways$ = this.gatewayService.getGateways(this.buildingId).pipe(
      map((gatewaysResponse: Gateways) => gatewaysResponse.gateway),
      switchMap((gateways: Gateway[]) => from(gateways)),
      toArray(),
      map((gateways) => gateways.sort(this.compareGateways)),
      finalize(() => this.endBusy())
    );
  }

  private getDefaultHealthForGateway(gateway: Gateway): Observable<GatewayHealth> {
    return of(this.gatewayService.produceCleanGatewayHealth(gateway.address));
  }

  private compareGateways = (gateA: GatewayWithHealth, gateB: GatewayWithHealth) => {
    if (gateA.id > gateB.id) {
      return 1;
    } else if (gateA.id < gateB.id) {
      return -1;
    } else {
      return 0;
    }
  };

  public deleteGateway(gatewayId): void {
    const data = new ConfirmDialogData('Gateway is going to be deleted', 'Delete gateway');
    this.confirmationDialogService.open(data).subscribe((confirm) => {
      if (confirm) {
        this.startBusy();
        this.gatewayService.deleteGateway(gatewayId).subscribe({
          next: () => {
            this.endBusy();
            this.loadGateways();
          },
          error: (response: HttpErrorResponse) => {
            this.toastService.error({
              message: this.parseErrorWhenDeletingGateway(response),
              dataCy: 'delete-gateway-error'
            });
            this.endBusy();
          },
          complete: () => {
            this.endBusy();
          }
        });
      }
    });
  }

  private parseErrorWhenDeletingGateway(response?: HttpErrorResponse): string {
    const defaultMessage = 'Unable to delete gateway. Unexpected error occurred when removing iot thing from AWS';
    if (!response) {
      return defaultMessage;
    }
    if (typeof response.error === 'string') {
      return response.error;
    }
    const message = (response?.error?.message as string) || response?.message || defaultMessage;
    return message.includes('tellus-bacnet')
      ? 'Unable to delete gateway. Looks like BACnet service is not running!'
      : message;
  }

  public startBusy(): void {
    this.isGatewayListBusy.set(true);
  }

  public endBusy(): void {
    this.isGatewayListBusy.set(false);
  }

  public reload(): void {
    this.resetNewGatewayForm();
    this.loadGateways();
  }

  public resetGateway(gatewayId: number): void {
    const data = new ConfirmDialogData('Reset changes?', 'Reset Gateway');
    this.confirmationDialogService.open(data).subscribe((confirm) => {
      if (confirm) {
        if (gatewayId == null) {
          this.resetNewGatewayForm();
        } else {
          this.onGatewayReset$.next(gatewayId);
        }
      }
    });
  }

  private resetNewGatewayForm(): void {
    this.newGatewayDetails = this.gatewayService.produceCleanGateway(this.buildingId);
    this.newGatewayDetails.original = Gateway.clone(this.newGatewayDetails);
  }

  public onAddGateway(gateway: Gateway): void {
    this.startBusy();
    this.gatewayService.addGateway(gateway).subscribe({
      next: () => {
        this.endBusy();
        this.reload();
      },
      error: (error) => {
        console.log(error);
        const errorMessage =
          error?.error?.message || (typeof error?.error === 'string' ? error.error : error?.message) || '';
        this.toastService.error({
          message: 'The gateway address you provided could not be added to this building. ' + errorMessage,
          dataCy: 'add-gateway-error'
        });
        this.endBusy();
      }
    });
  }

  public onUpdateGateway(gateway: Gateway): void {
    if (gateway.original) {
      this.startBusy();
      this.performUpdate(gateway);
    }
  }

  private performUpdate(gateway: Gateway): void {
    this.gatewayService.updateGateway(gateway.id, gateway).subscribe({
      next: () => {
        // Update original since we have saved
        gateway.original = Gateway.clone(gateway);
        this.afterUpdate$.next(gateway);
      },
      error: (err) => {
        this.toastService.error({
          message: 'The update for gateway failed. ' + err.error,
          dataCy: 'update-gateway-error'
        });
        this.onError$.next(gateway);
        this.endBusy();
      },
      complete: () => {
        this.endBusy();
      }
    });
  }

  public updateGatewayFirmwareVersions(): void {
    this.gatewayService.getGatewayFirmwareVersions(this.buildingId).subscribe((gatewayFirmwareVersions) => {
      gatewayFirmwareVersions.forEach((gatewayFirmwareVersion) => {
        FirmwareVersion.addVersion(
          gatewayFirmwareVersion.id,
          gatewayFirmwareVersion.firmwareVersion,
          gatewayFirmwareVersion.legacySupport,
          gatewayFirmwareVersion.nmsSupport
        );
      });
    });
  }

  private parseErrorMessage(error: any): string {
    let errorMessage =
      error?.error?.message ||
      (typeof error?.error === 'string' ? error.error : error?.message) ||
      'An unexpected error occurred.';
    if (error?.status === 400 && error?.error) {
      const { missingApps, missingSnaps } = error.error;
      const missingAppsFormatted =
        Array.isArray(missingApps) && missingApps.length > 0 ? missingApps.join(', ') : 'None';
      const missingSnapsFormatted =
        Array.isArray(missingSnaps) && missingSnaps.length > 0 ? missingSnaps.join(' OR ') : 'None';

      if (missingAppsFormatted === 'None' && missingSnapsFormatted === 'None') {
        return errorMessage;
      }
      return `-- Gateway has not been setup correctly -- Missing Apps: ${missingAppsFormatted}, \nMissing Snaps: ${missingSnapsFormatted}`;
    }

    if (errorMessage.includes('serial')) {
      errorMessage = 'Invalid Serial Number';
    }
    return `Could not retrieve apps installed on gateway. ${errorMessage}`;
  }

  public getGatewaySnap(gateway: Gateway): void {
    this.startBusy();
    this.isLoading.set(true);
    this.gatewayService.getGatewayInstalledSnap(gateway.serialNumber).subscribe({
      next: (response) => {
        this.applyInstalledSnapResponse(gateway, response);

        if (gateway.original.gatewaySnap !== response.installedSnap) {
          this.toastService.info({
            message: "Gateway Snap changed, 'Firmware' field updated, click 'Update' to save changes",
            dataCy: 'gateway-snap-update-info'
          });
        }
        this.afterUpdate$.next(gateway);
      },
      error: (error) => {
        const errorMessage = this.parseErrorMessage(error);
        this.toastService.error({
          message: errorMessage,
          dataCy: 'gateway-retrieve-installed-apps-error'
        });
        this.onError$.next(gateway);
        this.endBusy();
        this.isLoading.set(false);
      },
      complete: () => {
        this.endBusy();
        this.isLoading.set(false);
      }
    });
  }

  public export(): void {
    this.gatewayService.downloadExport(this.buildingId).subscribe((data) => {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const a = document.createElement('a');
      const url = window.URL.createObjectURL(blob);

      a.href = url;
      a.download = 'Building Gateway Extract';
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  /**
   * Sync the gateway's configuration with Rigado's edge direct
   */
  public onConfigureWithRigado(gateway: Gateway): void {
    this.startBusy();

    this.gatewayService.compareConfig(gateway.id).subscribe(
      (gatewayConfigComparison) => {
        if (gateway.original) {
          const data = new ConfirmDialogData('Configure Gateway', 'Proceed with configuring the gateway with Rigado?');
          this.confirmationDialogService.open(data).subscribe((confirm) => {
            if (confirm) {
              this.performConfigure(gateway);
            } else {
              this.endBusy();
            }
          });
        }
      },
      () => {
        this.toastService.error({
          message: 'Could not retrieve gateway config. ' + this.errorMessage,
          dataCy: 'compare-gateway-error'
        });
        this.onError$.next(gateway);
        this.endBusy();
      }
    );
  }

  private parseErrorWhenDisplayingMigrationConfig(response?: HttpErrorResponse | string): string {
    const defaultMessage =
      'Unable to retrieve gateway configuration for migration. Unexpected error occurred when retrieving gateway config';
    if (typeof response === 'string') {
      return response;
    }
    if (!response) {
      return defaultMessage;
    }
    if (typeof response.error === 'string') {
      return response.error;
    }
    return (response?.error?.message as string) || response?.message || defaultMessage;
  }

  onGatewayMigration(gateway: Gateway): void {
    this.startBusy();
    this.gatewayService.compareGatewayForMigration(this.buildingId, gateway.serialNumber).subscribe({
      next: (gatewayConfigComparison) => {
        const dialogData: DialogData = {
          gateway: { ...gateway },
          left: JSON.parse(gatewayConfigComparison?.portalConfig),
          right: JSON.parse(gatewayConfigComparison?.rigadoConfig),
          isEqual: gatewayConfigComparison?.portalConfig === gatewayConfigComparison?.rigadoConfig,
          disableConfirm: false,
          confirmCallback: () => this.confirmMigration(gateway)
        };
        this.dialog.open(CompareConfigDialogComponent, {
          width: '1200px',
          data: dialogData
        });
        this.endBusy();
      },
      error: (err: HttpErrorResponse | string) => {
        const errorMessage = this.parseErrorWhenDisplayingMigrationConfig(err);
        this.toastService.error({
          message: 'Could not retrieve gateway config. ' + errorMessage,
          dataCy: 'compare-gateway-error'
        });
        this.onError$.next(gateway);
        this.endBusy();
      }
    });
  }

  private performConfigure(gateway: Gateway): void {
    this.gatewayService.configureWithRigado(gateway.id, gateway).subscribe(
      () => {
        // Update original since we have saved
        gateway.original = Gateway.clone(gateway);
        this.afterUpdate$.next(gateway);
      },
      () => {
        this.toastService.error({
          message: 'The update for gateway failed. ' + this.errorMessage,
          dataCy: 'configure-gateway-error'
        });
        this.onError$.next(gateway);
        this.endBusy();
      },
      () => {
        this.endBusy();
        this.toastService.info({
          message: 'Gateway configured with Rigado successfully',
          dataCy: 'configure-gateway-info'
        });
      }
    );
  }

  public restartGateway(gateway: Gateway): void {
    const data: ConfirmDialogData = new ConfirmDialogData(
      'Are you sure you want to restart the gateway? (Note: a 30 minute cooldown period applies)',
      'Restart gateway'
    );
    this.confirmationDialogService.open(data).subscribe((confirm) => {
      if (confirm) {
        this.startBusy();
        this.gatewayService
          .requestGatewayRestart(gateway.address, this.gatewayService.produceCleanGatewayHealth(gateway.address))
          .subscribe(
            () => {
              this.endBusy();
            },
            () => {
              this.toastService.warning({
                message: 'The gateway could not be restarted. Please try again later.',
                dataCy: 'restart-gateway-warning'
              });
              this.endBusy();
            }
          );
      }
    });
  }

  public generateKeys(gateway: Gateway): void {
    const data = new ConfirmDialogData(`Generate keys for gateway ${gateway.address}?`, 'Generate keys');
    this.confirmationDialogService.open(data).subscribe((confirm) => {
      if (confirm) {
        this.startBusy();
        this.gatewayService.generateKeys(gateway.id).subscribe(
          () => {
            gateway.hasKeys = true;
            this.endBusy();
          },
          () => {
            this.toastService.warning({
              message: 'The gateway has keys already generated for it. Keys may only be generated once!',
              dataCy: 'generate-keys-gateway-warning'
            });
            this.endBusy();
          }
        );
      }
    });
  }

  public onCompareGateways(gateway: Gateway): void {
    this.startBusy();
    this.isLoading.set(true);
    let disableConfigure = false;
    this.gatewayService.getGatewayInstalledSnap(gateway.serialNumber).subscribe({
      next: (response) => {
        this.applyInstalledSnapResponse(gateway, response);
        if (response.installedSnap !== gateway.original.gatewaySnap) {
          disableConfigure = true;
          this.toastService.warning({
            message: 'Gateway snap changed, please set correct firmware version',
            dataCy: 'gateway-retrieve-installed-apps-error'
          });
        } else if (
          gateway.firmwareVersionId != null &&
          FirmwareVersion.fromId(gateway.firmwareVersionId)?.nmsSupport === true &&
          !response.supportsNmsFirmware
        ) {
          disableConfigure = true;
          this.toastService.warning({
            message: 'Gateway does not support selected firmware version with currently installed ORT Wirepas snap version',
            dataCy: 'gateway-retrieve-installed-apps-error'
          });
        }
      },
      error: (error) => {
        disableConfigure = true;
        const errorMessage = this.parseErrorMessage(error);
        this.toastService.error({
          message: errorMessage,
          dataCy: 'gateway-retrieve-installed-apps-error'
        });
        this.endBusy();
        this.isLoading.set(false);
      },
      complete: () => {
        this.endBusy();
        this.isLoading.set(false);
        this.compareConfig(gateway, disableConfigure);
      }
    });
  }

  private applyInstalledSnapResponse(gateway: Gateway, response: GatewayInstalledSnap): void {
    gateway.gatewaySnap = response.installedSnap;
    gateway.installedSnapRevision = response.installedSnapRevision;
    gateway.supportsNmsFirmware = response.supportsNmsFirmware;
  }

  /**
   * Fetch the gateway configuration that is stored in RDS and on edge direct
   * and present them side-by-side in dialog to allow the user to compare both configurations
   * @param gateway - Gateway that is queried,
   * @param disableConfirm - Disable the confirm button in the dialog
   */
  private compareConfig(gateway: Gateway, disableConfirm: boolean): void {
    this.startBusy();
    this.isLoading.set(true);
    this.gatewayService.compareConfig(gateway.id).subscribe(
      (gatewayConfigs) => {
        const dialogData: DialogData = {
          gateway: { ...gateway },
          left: JSON.parse(gatewayConfigs?.portalConfig),
          right: JSON.parse(gatewayConfigs?.rigadoConfig),
          isEqual: gatewayConfigs?.portalConfig === gatewayConfigs?.rigadoConfig,
          disableConfirm,
          confirmCallback: () => this.onConfigureWithRigado(gateway)
        };
        this.dialog.open(CompareConfigDialogComponent, {
          width: '1200px',
          data: dialogData
        });
      },
      () => {
        this.toastService.error({
          message: 'Unable to retrieve gateway configuration. Please try again',
          dataCy: 'compare-gateway-error'
        });
        this.endBusy();
        this.isLoading.set(false);
      },
      () => {
        this.endBusy();
        this.isLoading.set(false);
      }
    );
  }

  private confirmMigration(gateway: Gateway) {
    this.startBusy();
    this.gatewayService.migrateGatewayConfiguration(this.buildingId, gateway).subscribe({
      next: () => {
        this.toastService.info({
          message: 'Gateway migrated successfully',
          dataCy: 'migrate-gateway-success'
        });
        this.loadGateways();
      },
      error: (err) => {
        const errorMessage =
          err?.error?.message ||
          (typeof err?.error === 'string' ? err.error : err?.message) ||
          'An unexpected error occurred.';
        this.toastService.error({
          message: 'Gateway migration failed. ' + errorMessage,
          dataCy: 'migrate-gateway-error'
        });
        this.endBusy();
      },
      complete: () => {
        this.endBusy();
      }
    });
  }
}

type GatewayWithHealth = Gateway & { health?: GatewayHealth };
